import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import { syncWidgetData } from '@/lib/widget-bridge'
import type { DashboardData } from '@/lib/types'

export function useDashboard() {
  const query = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardData>('/api/dashboard'),
  })

  useEffect(() => {
    if (query.data) {
      syncWidgetData(query.data).catch(() => {})
    }
  }, [query.data])

  return query
}

export function useSnoozeFollowUp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) =>
      api<{ snoozed: boolean }>(`/api/contacts/${contactId}/snooze-followup`, { method: 'POST' }),
    onMutate: async (contactId) => {
      await qc.cancelQueries({ queryKey: ['dashboard'] })
      const prev = qc.getQueryData<DashboardData>(['dashboard'])
      if (prev) {
        qc.setQueryData<DashboardData>(['dashboard'], {
          ...prev,
          overdueContacts: prev.overdueContacts.filter((c) => c.id !== contactId),
          stats: { ...prev.stats, overdueCount: Math.max(0, prev.stats.overdueCount - 1) },
        })
      }
      return { prev }
    },
    onError: (_err, _id, context) => {
      if (context?.prev) qc.setQueryData(['dashboard'], context.prev)
      toast.error(i18n.t('toast.error'))
    },
    onSuccess: () => {
      toast.success(i18n.t('followUp.snoozed', 'Snoozed for 1 week'))
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDismissFollowUp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) =>
      api<{ dismissed: boolean }>(`/api/contacts/${contactId}/dismiss-followup`, {
        method: 'POST',
      }),
    onMutate: async (contactId) => {
      await qc.cancelQueries({ queryKey: ['dashboard'] })
      const prev = qc.getQueryData<DashboardData>(['dashboard'])
      if (prev) {
        qc.setQueryData<DashboardData>(['dashboard'], {
          ...prev,
          overdueContacts: prev.overdueContacts.filter((c) => c.id !== contactId),
          stats: { ...prev.stats, overdueCount: Math.max(0, prev.stats.overdueCount - 1) },
        })
      }
      return { prev }
    },
    onError: (_err, _id, context) => {
      if (context?.prev) qc.setQueryData(['dashboard'], context.prev)
      toast.error(i18n.t('toast.error'))
    },
    onSuccess: () => {
      toast.success(i18n.t('followUp.dismissed', 'Dismissed'))
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
